<template>
  <div class="tourCassette">
    <div :class="['tourCassette_contentsArea', { '-recommended': tour.recommend_point === 1 }]">
      <div
        class="tourCassette_leftArea"
        :style="{ '--height': `${rightAreaHeight}px` }"
      >
        <tk-link
          :class="['tourCassette_imageLink', { '-suppressHighlight': mobile }]"
          :href="makeDetailPageUrl(tour.code)"
          target="_blank"
        >
          <tk-image
            class="tourCassette_image"
            :image-path="mainImage.path"
            :alt="mainImage.caption"
            :suppress-highlight="mobile"
            lazy
            :lazy-loading-margin=300
          />
        </tk-link>
        <tk-image
          v-if="tour.recommend_point === 1"
          class="tourCassette_recommendationTag"
          :image-path="require('@ota-frontend/shared-assets/images/common/ico_recommend_tour.png')"
          alt="オススメpickup"
        />
        <div class="tourCassette_underSentence">
          {{ underImageSentence }}
        </div>
      </div>
      <div
        ref="rightArea"
        class="tourCassette_rightArea"
      >
        <tk-link
          class="tourCassette_titleLink"
          :href="makeDetailPageUrl(tour.code)"
          target="_blank"
        >
          <div class="tourCassette_title">
            {{ tour.title }}
          </div>
        </tk-link>
        <tour-summary
          v-if="tour.recommend_point === 1"
          class="tourCassette_summary"
          :tour="tour"
          :destination="false"
          hotels-with-destination
          size="large"
        />
        <div class="tourCassette_tabArea">
          <tk-tabs
            v-if="durationTabs"
            nav-tabs-class="tourCassette_similarTourTabs"
            :active-class="`active ${tour.recommend_point === 1 ? '-recommend' : '-normal'}`"
            content-class="tab-content"
            :active-tab-index="activeDurationTabIndex"
          >
            <tk-tab
              v-for="similarTour in similarTours"
              :key="similarTour.tourCode"
              :title="`${similarTour.duration}日間`"
            >
              <div class="tourCassette_priceArea">
                <div
                  v-if="!!similarTour.discounted"
                  class="tourCassette_price"
                >
                  <p class="tourCassette_priceLabel -red">支払実額</p>
                  <p class="tourCassette_priceAmount -red -large">{{ makeDisplayPrice(similarTour.discounted) }}</p>
                </div>
                <div class="tourCassette_price">
                  <p class="tourCassette_priceLabel">旅行代金</p>
                  <p class="tourCassette_priceAmount">{{ makeDisplayPrice(similarTour) }}</p>
                </div>
              </div>
              <div class="tourCassette_buttonArea">
                <tk-tooltips
                  v-if="tour.recommend_point !== 1"
                  class="tourCassette_overviewBoxArea"
                  :trigger="mobile ? 'click' : 'hover'"
                >
                  <template v-slot:tool>
                    <div class="tourCassette_overviewBox">
                      ツアー概要
                    </div>
                    <tk-down-arrow class="tourCassette_overviewBoxDownArrow" />
                  </template>
                  <template v-slot:tips>
                    <div class="tourCassette_tourSummaryHeaderArea">
                      <span class="tourCassette_tourSummaryHeader">ツアーコード：{{ tour.code }}</span>
                    </div>
                    <tour-summary
                      class="tourCassette_tourSummary"
                      :tour="tour"
                      icon-border
                    />
                  </template>
                </tk-tooltips>
                <div class="tourCassette_detailButton">
                  <tk-link
                    class="tourCassette_detailUrl"
                    :href="makeDetailPageUrl(similarTour.tourCode)"
                    target="_blank"
                  >
                    ツアー詳細を見る
                  </tk-link>
                </div>
              </div>
            </tk-tab>
          </tk-tabs>
          <template v-else>
            <div class="tourCassette_priceArea">
              <div
                v-if="!!tour.price.discounted"
                class="tourCassette_price"
              >
                <p class="tourCassette_priceLabel -red">支払実額</p>
                <p class="tourCassette_priceAmount -red -large">{{ makeDisplayPrice(tour.price.discounted) }}</p>
              </div>
              <div class="tourCassette_price">
                <p class="tourCassette_priceLabel">旅行代金</p>
                <p class="tourCassette_priceAmount">{{ makeDisplayPrice(tour.price) }}</p>
              </div>
            </div>
            <div class="tourCassette_buttonArea">
              <tk-tooltips
                v-if="tour.recommend_point !== 1"
                class="tourCassette_overviewBoxArea"
                :trigger="mobile ? 'click' : 'hover'"
              >
                <template v-slot:tool>
                  <div class="tourCassette_overviewBox">
                    ツアー概要
                  </div>
                  <tk-down-arrow class="tourCassette_overviewBoxDownArrow" />
                </template>
                <template v-slot:tips>
                  <div class="tourCassette_tourSummaryHeaderArea">
                    <span class="tourCassette_tourSummaryHeader">ツアーコード：{{ tour.code }}</span>
                  </div>
                  <tour-summary
                    class="tourCassette_tourSummary"
                    :tour="tour"
                    icon-border
                  />
                </template>
              </tk-tooltips>
              <div class="tourCassette_detailButton">
                <tk-link
                  class="tourCassette_detailUrl"
                  :href="makeDetailPageUrl(tour.code)"
                  target="_blank"
                >
                  ツアー詳細を見る
                </tk-link>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uniq } from 'lodash'
import {
  TkImage,
  TkLink,
  TkDownArrow,
  TkTabs,
  TkTab,
  TkTooltips,
} from '@ota-frontend/ui'
import TourSummary from '@/components/TourSummary'

export default {
  name: 'TourCassette',
  components: {
    TkImage,
    TkLink,
    TkDownArrow,
    TkTabs,
    TkTab,
    TkTooltips,
    TourSummary,
  },
  props: {
    tour: {
      type: Object,
      required: true,
    },
    hotelsWithDestination: {
      type: Boolean,
      default: false,
    },
    makeDetailPageUrl: {
      type: Function,  // tourCode => url
      required: true,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
    durationTabs: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rightAreaHeight: 0, // PCで右側の高さに合わせて画像の高さを決めるのに使用
    }
  },
  computed: {
    mainImage() {
      return this.tour.images.find(image => image.order === 1) || this.tour.images[0]
    },
    underImageSentence() {
      return `${this.destinationText}（${this.tour.destinations[0].country_name}） ツアー${this.departureName}発 ${this.tour.duration}日間`
    },
    destinationText() {
      const formattedDestinations = this.tour.destinations
        .filter(d => d.city_name)
        .map(d => d.district_name ? `${d.city_name}(${d.district_name})` : d.city_name)
      return uniq(formattedDestinations).join(',')
    },
    departureName() {
      const departureNames = this.tour.domestic && this.tour.train
        ? this.tour.departures_org.map(departure => departure.name)
        : this.tour.departures.map(departure => departure.name)
      const departureName = departureNames.join(',').replace(/東京\(成田\)/g, '成田').replace(/東京\(羽田\)/g, '羽田')
      return departureName
    },
    // ツアー情報はモデル化するべき
    similarTours() {
      const mainTour = {
        tourId: '-1',
        tourCode: this.tour.code,
        duration: this.tour.duration,
        visitorCode: this.tour.visitor_code,
        minPrice: this.tour.price.min,
        maxPrice: this.tour.price.max,
        ...(this.tour.price.discounted
          ? {
            discounted: {
              minPrice: this.tour.price.discounted.min,
              maxPrice: this.tour.price.discounted.max,
            },
          }
          : {}),
      }

      return [...this.tour.similar_tours, mainTour]
        .map(tour => ({
          ...tour,
          diffOfDuration: Math.abs(tour.duration - mainTour.duration)
        }))
        .sort((a, b) => a.diffOfDuration > b.diffOfDuration ? 1 : -1)
        .slice(0, 5)
        .sort((a, b) => a.duration > b.duration ? 1 : -1)
    },
    activeDurationTabIndex() {
      return this.similarTours.findIndex(s => s.tourCode === this.tour.code)
    },
  },
  mounted() {
    // TourSummaryが読み込まれる前のheightが取れてしまうため$nextTickを使用してレンダリング完了を待つ
    this.$nextTick(() => {
      this.rightAreaHeight = this.$refs.rightArea ? this.$refs.rightArea.clientHeight : 0
    })
  },
  methods: {
    makeDisplayPrice(tour) {
      return uniq([tour.minPrice || tour.min, tour.maxPrice || tour.max])
        .filter(Boolean)
        .map(p => `${p.toLocaleString()}円`)
        .join('～')
    },
  },
}
</script>

<style scoped lang="scss">
.tourCassette {
  position: relative;
  font-size: 11px;
  border-top: 0;
  border: 0;
  box-sizing: border-box;
}

.tourCassette_contentsArea {
  display: flex;
  height: 270px;
  padding: 15px;
  color: #333;
  border: 1px solid #CCCCCC;
  background: rgb(255, 255, 255);

  &.-recommended {
    height: auto;
    min-height: 270px;
    border: 2px solid rgb(255, 220, 58);
    background: rgb(255, 255, 244);
  }

  @media (max-width: 657px) {
    display: block;
    height: auto;
    padding: 0;
  }
}

.tourCassette_leftArea {
  position: relative;
  width: 60%;
  height: var(--height);

  @media (max-width: 657px) {
    width: 100%;
    height: 170px;
  }
}
.tourCassette_imageLink {
  &.-suppressHighlight {
    &:hover {
      opacity: 1;
    }
  }
}
.tourCassette_image {
  height: 100%;
  width: 100%;
  object-fit: cover;
}
.tourCassette_underSentence {
  position: absolute;
  bottom: 0;
  width: 100%;
  padding: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 14px;
}

.tourCassette_rightArea {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 80%;
  padding-left: 15px;
  font-size: 15px;

  @media (max-width: 657px) {
    gap: 12px;
    width: 100%;
    padding: 15px 17px 13px;
  }
}
.tourCassette_titleLink {
  color: #333;
}
.tourCassette_title {
  position: relative;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  margin: 0;
  height: auto;
  padding: 0;
  text-decoration-line: none;
  text-decoration-style: initial;
  text-decoration-color: initial;
  font-size: 17px;
  line-height: 26px;
  letter-spacing: 0.8px;
  font-weight: 600;
  text-decoration: underline;

  @media (max-width: 657px) {
    -webkit-line-clamp: 3;
    overflow: hidden;
  }
}
.tourCassette_summary {
  font-size: 14px;
  font-family: 游ゴシック Medium,游ゴシック,YuGothic,ヒラギノ角ゴ ProN W3,Hiragino Kaku Gothic ProN,メイリオ,Meiryo,sans-serif;
}
.tourCassette_tabArea {
  margin-top: auto;
}
.tourCassette_priceArea {
  margin: 10px auto;
}
.tourCassette_price {
  display: flex;
  align-items: flex-start;
  margin-bottom: 5px;
}
.tourCassette_priceLabel {
  color: #63666a;
  border: 1px solid #63666a;
  background: #ffffff;
  text-align: center;
  font-size: 11px;
  border-radius: 15px;
  padding: 2px 5px;
  width: 70px;
  margin: auto 0;

  &.-red {
    color: #f9423a;
    border-color: #f9423a;
  }
}
.tourCassette_priceAmount {
  margin-top: 0px;
  font-weight: bold;
  font-size: 18px;
  padding-left: 5px;
  color: #63666a;
  margin-bottom: 0;

  &.-red {
    color: #f9423a;
  }
  &.-large {
    font-size: 20px;
  }
}
.tourCassette_buttonArea {
  display: flex;
  justify-content: space-between;
  gap: 5px;
  margin-top: 5px;
  width: 100%;
}
.tourCassette_overviewBoxArea {
  position: relative;
  width: 140px;
}
.tourCassette_overviewBox {
  display: flex;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-color: #d2d2d2;
  border-radius: 4px;
  background-color: #f4f4f4;
  color: #63666a;
  font-size: 14px;
  font-weight: bold;
  padding: 12px 22px;
  height: 40px;
  cursor: pointer;
  width: 140px;
}
.tourCassette_overviewBoxDownArrow {
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  -webkit-transform: translateY(-50%);
  -ms-transform: translateY(-50%);
}
.tourCassette_tourSummaryHeaderArea {
  display: table;
  width: 509px;
  height: 44px;
  padding: 0 18px;
  background-color: #dedfdf;
  font-size: 13px;

  @media (max-width: 657px) {
    width: 320px;
  }
}
.tourCassette_tourSummaryHeader {
  display: table-cell;
  vertical-align: middle;
}
.tourCassette_tourSummary {
  width: 509px;
  padding: 16px;
  background-color: white;

  @media (max-width: 657px) {
    width: 320px;
  }
}
.tourCassette_detailButton {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #F9423A;
  border-radius: 4px;
  box-sizing: border-box;
  color: #ffffff;
  cursor: pointer;
  font-weight: bold;
  text-align: center;
  height: 40px;
  padding: 0px 10px 0px 0px;
  font-size: 14px;
  line-height: 1;

  .tourCassette_detailUrl {
    display: block;
    width: 100%;
    font-weight: normal;
    color: #ffffff;
    text-decoration: none;
  }
}

.tourCassette_recommendationTag {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 60px;
  height: 60px;
  z-index: 1;
}
</style>

<style lang="scss">
.tourCassette_similarTourTabs {
  display: flex;
  width: 100%;
  padding: 0 5px;
  border-bottom: solid 1px #d2d2d2;
  position: relative;

  & li {
    display: flex;
    display: -webkit-box;
    display: -moz-box;
    display: -ms-flexbox;
    display: -webkit-flex;
    justify-content: center;
    float: left;
    border-radius: 4px 4px 0 0;
    background: #e7e7e7;
    border: 1px solid #d2d2d2;
    border-bottom: none;
    padding: 10px 0;
    width: 15%;
    margin-right: 1%;
    font-size: 13px;
    cursor: pointer;

    &.active {
      position: relative;
      color: #333;
      border-bottom: none;
      font-weight: bold;
      top: 1px;

      &.-normal {
        background: white;
      }
      &.-recommend {
        background: rgb(255, 255, 244);
      }
    }
  }
}
</style>
